import JsBarcode from "jsbarcode";
import moment from "moment";
import "moment/locale/es";
import PhoneMask from "../Components/phoneMask";

// Clonado de Pdf/etiquetasMovimientoInventarioPdf.js (mismo layout de etiqueta
// termica 75x50mm con jsbarcode), adaptado a los datos de una unidad de Activo
// Fijo (FOLIO / UBICACIÓN / FECHA DE ALTA en vez de UNIDAD / LOTE / CADUCIDAD).
// Nota: el original tiene un bug donde el barcode siempre codifica el literal
// "P9318H" en vez del valor real — aqui se corrige usando el folio real de
// la unidad, ya que aqui el folio es el dato central de la etiqueta.

moment.locale("es");

const mmToPt = (mm) => mm * 2.83465;

const ANCHO_MM = 75;
const ALTO_MM = 50;
const MARGEN_MM = 2.5;

const generarBarcodeDataURL = (valor) => {
  if (!valor) return null;
  if (typeof document === "undefined") return null;
  const JsBarcodeLib =
    (typeof JsBarcode !== "undefined" && JsBarcode) ||
    (typeof window !== "undefined" && window.JsBarcode) ||
    null;
  if (!JsBarcodeLib) return null;
  const canvas = document.createElement("canvas");
  try {
    JsBarcodeLib(canvas, String(valor), {
      format: "CODE128",
      displayValue: false,
      lineColor: "#000",
      width: 3,
      height: 80,
      margin: 0,
    });
    return canvas.toDataURL("image/png");
  } catch (e) {
    return null;
  }
};

const GAMMA_OSCURIDAD = 2.2;
const convertirAGrisesBase64 = (base64) =>
  new Promise((resolve) => {
    if (!base64 || typeof document === "undefined") return resolve(base64);
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const d = imageData.data;
        for (let i = 0; i < d.length; i += 4) {
          const gris = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
          const oscuro = Math.round(255 * Math.pow(gris / 255, GAMMA_OSCURIDAD));
          d[i] = d[i + 1] = d[i + 2] = oscuro;
        }
        ctx.putImageData(imageData, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch (e) {
        resolve(base64);
      }
    };
    img.onerror = () => resolve(base64);
    img.src = base64;
  });

const nombreEmpresa = (empresa = {}) => {
  if (empresa.nombreComercial) return empresa.nombreComercial;
  if (empresa.persona) return empresa.nombre || "";
  return `${empresa.nombre || ""} ${empresa.apellidoPaterno || ""} ${empresa.apellidoMaterno || ""}`.trim();
};

const fontSizeDescripcion = (texto) => {
  const len = (texto || "").length;
  if (len <= 40) return 10;
  if (len <= 80) return 9;
  if (len <= 140) return 8;
  if (len <= 220) return 7;
  return 6;
};

const construirEtiqueta = (unidad, empresa, conDatosEmpresa) => {
  const folio = unidad.serieNumero || unidad.numero || "";
  const descripcion = (unidad.descripcion || "").trim().toUpperCase();
  const ubicacionFisica = unidad.ubicacionFisica || "";
  const fechaAdquisicion = unidad.fechaAdquisicion
    ? moment(unidad.fechaAdquisicion).format("DD/MM/YYYY")
    : "";

  const barcodeDataURL = generarBarcodeDataURL(folio);

  const metaRows = [];
  if (ubicacionFisica) metaRows.push(["UBICACIÓN:", ubicacionFisica]);
  if (fechaAdquisicion) metaRows.push(["FECHA DE ALTA:", fechaAdquisicion]);

  const metaTable =
    metaRows.length > 0
      ? {
        table: {
          widths: ["auto", "*"],
          body: metaRows.map(([l, v]) => [
            { text: l, fontSize: 5.5, bold: true, color: "#000", border: [false, false, false, false], margin: [0, 0, 2, 0] },
            { text: v, fontSize: 5.5, bold: true, color: "#000", border: [false, false, false, false], alignment: "left" },
          ]),
        },
        layout: "noBorders",
      }
      : null;

  const tieneLogo = !!empresa?.logo?.base64Completa;
  let header = null;

  if (conDatosEmpresa) {
    const colLogo = tieneLogo
      ? { image: "logoEtiqueta", fit: [mmToPt(15), mmToPt(15)], width: mmToPt(11) }
      : { text: "", width: mmToPt(0.1) };

    const colEmpresa = {
      width: "*",
      stack: [
        { text: nombreEmpresa(empresa) || "", fontSize: 7, bold: true, alignment: "center", lineHeight: 1.05 },
        empresa?.telefono
          ? { text: `Tel: ${PhoneMask(empresa.telefono)}`, fontSize: 5.5, alignment: "center", margin: [0, 0, 0, 0] }
          : null,
      ].filter(Boolean),
    };

    header = {
      columns: tieneLogo ? [colLogo, colEmpresa] : [colEmpresa],
      columnGap: 3,
      margin: [0, 0, 0, 2],
    };
  }

  const colBarcode = {
    width: metaTable ? mmToPt(40) : "*",
    margin: [0, 8, 0, 1],
    stack: [
      barcodeDataURL
        ? { image: barcodeDataURL, fit: [mmToPt(34), mmToPt(7)], alignment: "left" }
        : { text: folio || "-", margin: [0, 2, 0, 0], fontSize: 12, bold: true, alignment: "left" },
      { text: folio || "", fontSize: 9, bold: true, characterSpacing: 1, alignment: "left", margin: [0, 2, 0, 0] },
    ],
  };

  const medio = {
    columns: metaTable ? [colBarcode, { ...metaTable, width: "*" }] : [colBarcode],
    columnGap: 3,
    margin: [0, 0, 0, 2],
  };

  const desc = {
    text: descripcion || "",
    fontSize: fontSizeDescripcion(descripcion || ""),
    bold: true,
    alignment: "left",
    lineHeight: 1.05,
    margin: [0, 1, 0, 0],
  };

  const stack = [];
  if (header) stack.push(header);
  stack.push(medio);
  stack.push(desc);
  return stack;
};

const etiquetaActivoFijoPdf = async (data, opciones = {}) => {
  const { unidades, empresa } = data;
  const conDatosEmpresa = !!opciones.conDatosEmpresa;
  const listaUnidades = (unidades || []).filter((u) => u);

  const margen = [
    mmToPt(MARGEN_MM),
    mmToPt(MARGEN_MM + (conDatosEmpresa ? 0 : 10)),
    mmToPt(MARGEN_MM),
    mmToPt(MARGEN_MM),
  ];

  const content = [];
  listaUnidades.forEach((unidad, idx) => {
    const etiqueta = construirEtiqueta(unidad, empresa, conDatosEmpresa);
    if (idx > 0) {
      content.push({ text: "", pageBreak: "before" });
    }
    content.push(...etiqueta);
  });

  if (content.length === 0) {
    content.push({
      text: "No hay activos para generar etiquetas",
      fontSize: 9,
      alignment: "center",
      margin: [0, 20, 0, 0],
    });
  }

  const dd = {
    pageSize: { width: mmToPt(ANCHO_MM), height: mmToPt(ALTO_MM) },
    pageMargins: margen,
    content,
    defaultStyle: { fontSize: 8 },
  };

  if (empresa?.logo?.base64Completa) {
    const logoMono = await convertirAGrisesBase64(empresa.logo.base64Completa);
    dd.images = { logoEtiqueta: logoMono };
  }

  return dd;
};

export default etiquetaActivoFijoPdf;
